import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import consumer from "../channels/consumer"
import { subscribeToSessions } from "../channels/sessions_channel"
import { subscribeToScores } from "../channels/scores_channel"

export default class extends Controller {
  static values = { tenants: Array, sessions: Array }

  connect() {
    this.seen = new Set()
    this.subscriptions = this.tenantsValue.map(id => subscribeToSessions(id, data => this.received(data)))
    this.subscriptions.push(...this.sessionsValue.map(id => subscribeToScores(id, data => this.received(data))))
    this.toast = document.getElementById("liveToast")
    this.hideToast()
  }

  disconnect() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
    clearTimeout(this.toastTimer)
    clearTimeout(this.refreshTimer)
    if (!consumer.subscriptions.subscriptions.length) consumer.disconnect()
  }

  received(data) {
    if (data.event_id && this.seen.has(data.event_id)) return
    if (data.event_id) this.seen.add(data.event_id)
    if (this.seen.size > 200) this.seen.delete(this.seen.values().next().value)
    if (["session_updated", "score_recorded", "live_score_update"].includes(data.type)) this.showToast(data)
    if (data.type === "session_updated") this.updateBookingCount(data)
    // Read-only result and court pages re-render using their existing presenters.
    // Keep score-entry forms untouched while an operator is typing.
    if (document.querySelector("[data-live-refresh]") && !document.querySelector("form input:not([type=hidden]), form textarea")) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => this.refreshContent(), 800)
    }
  }

  async refreshContent() {
    try {
      const response = await fetch(window.location.href, { headers: { Accept: "text/html" } })
      if (!response.ok || !this.element.isConnected) return
      const page = new DOMParser().parseFromString(await response.text(), "text/html")
      const content = page.querySelector("#live-session-content")
      if (content && this.element.isConnected) {
        Turbo.renderStreamMessage(`<turbo-stream action="replace" target="live-session-content"><template>${content.outerHTML}</template></turbo-stream>`)
      }
    } catch (error) {
      console.warn("Unable to refresh live results", error)
    }
  }

  updateBookingCount(data) {
    document.querySelectorAll(`[data-session-id="${Number(data.session_id)}"]`).forEach(card => {
      const count = Number(data.num_players) || 0
      const max = Number(data.max_players) || 0
      const label = card.querySelector("[data-live-booked-count]")
      const free = card.querySelector("[data-live-free-count]")
      const bar = card.querySelector("[data-live-capacity-bar]")
      if (label) label.textContent = `${count} booked`
      if (free && max) {
        const spots = Math.max(0, max - count)
        free.textContent = `${spots} ${spots === 1 ? "spot" : "spots"} free${data.waitlist_count ? `, ${data.waitlist_count} waitlisted` : ""}`
      }
      if (bar && max) {
        bar.style.width = `${Math.min(100, count / max * 100)}%`
        bar.classList.toggle("bg-stone-900", count >= max)
        bar.classList.toggle("bg-emerald-600", count < max)
      }
    })
  }

  showToast(data) {
    if (!this.toast) return
    this.toast.querySelector("[data-toast-title]").textContent = data.title || "Session update"
    this.toast.querySelector("[data-toast-body]").textContent = data.body || data.status || ""
    this.toast.hidden = false
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => this.hideToast(), data.type === "live_score_update" ? 3500 : 6000)
  }

  hideToast() {
    if (this.toast) this.toast.hidden = true
  }
}
